import React from "react";
import { motion } from "framer-motion";
import { PhoneCall, SearchCheck, FileText, CheckCircle2 } from "lucide-react";
import { Reveal, Overline, MaskedHeading } from "@/components/ui-bits";
import { stagger, fadeUp, viewport } from "@/lib/motion";

const STEPS = [
  {
    icon: PhoneCall,
    title: "Free Consultation",
    text: "Understand your situation and what needs verifying.",
  },
  {
    icon: SearchCheck,
    title: "Investigation Begins",
    text: "Professional, independent research using investigative methodology.",
  },
  {
    icon: FileText,
    title: "Comprehensive Report",
    text: "Written findings, risk assessment, and a clear recommendation.",
  },
  {
    icon: CheckCircle2,
    title: "You Decide",
    text: "Armed with verified facts, you make the decision — confidently.",
  },
];

export default function HowItWorks() {
  return (
    <section
      id="how-it-works"
      className="relative bg-paper py-24 sm:py-32"
      data-testid="how-it-works-section"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <Reveal>
            <Overline index={3}>The Process</Overline>
          </Reveal>
          <MaskedHeading
            tag="h2"
            className="mt-6 font-display font-bold text-navy leading-[1.05] tracking-tight text-3xl sm:text-4xl lg:text-5xl"
            lines={["From Uncertainty", "to Clarity"]}
          />
        </div>

        <motion.div
          variants={stagger(0.15)}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          className="mt-16 grid md:grid-cols-4 gap-8 relative"
        >
          {/* connecting line */}
          <div className="hidden md:block absolute top-7 left-0 right-0 h-px bg-navy/15" />
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title}
                variants={fadeUp}
                className="relative"
                data-testid={`process-step-${i + 1}`}
              >
                <div className="relative z-10 w-14 h-14 flex items-center justify-center bg-navy text-gold">
                  <Icon size={24} />
                </div>
                <div className="mt-6 flex items-baseline gap-3">
                  <span className="font-mono text-gold text-sm">
                    0{i + 1}
                  </span>
                  <h3 className="font-display text-xl font-semibold text-navy">
                    {s.title}
                  </h3>
                </div>
                <p className="mt-3 text-ink/65 leading-relaxed text-[15px]">
                  {s.text}
                </p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
